import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "quiz_attempt_answers";

const useQuizAttempt = ({ questions = [], attemptId }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const getStorageKey = useCallback(() => {
    return `${STORAGE_KEY}_${attemptId}`;
  }, [attemptId]);

  const loadStoredAnswers = useCallback(() => {
    if (!attemptId) {
      return [];
    }

    try {
      const savedAnswers = localStorage.getItem(getStorageKey());

      if (!savedAnswers) {
        return [];
      }

      const parsedAnswers = JSON.parse(savedAnswers);

      if (!Array.isArray(parsedAnswers)) {
        return [];
      }

      return parsedAnswers.filter(
        (item) => item.questionId && typeof item.selectedAnswer === "string",
      );
    } catch (error) {
      console.error("Failed to load saved quiz answers", error);

      return [];
    }
  }, [attemptId, getStorageKey]);

  const [answers, setAnswers] = useState(() => loadStoredAnswers());

  /**
   * Restore answers when attempt changes
   */
  useEffect(() => {
    setAnswers(loadStoredAnswers());

    setCurrentQuestionIndex(0);
  }, [attemptId, loadStoredAnswers]);

  /**
   * Save answers
   */
  useEffect(() => {
    if (!attemptId) {
      return;
    }

    localStorage.setItem(getStorageKey(), JSON.stringify(answers));
  }, [answers, attemptId, getStorageKey]);

  /**
   * Current question
   */
  const currentQuestion = questions[currentQuestionIndex] || null;

  /**
   * Select answer
   */
  const handleAnswerSelect = useCallback((questionId, selectedAnswer) => {
    if (!questionId || !selectedAnswer) {
      return;
    }

    setAnswers((previous) => {
      const existingAnswer = previous.find(
        (item) => item.questionId === questionId,
      );

      if (existingAnswer) {
        return previous.map((item) =>
          item.questionId === questionId
            ? {
                ...item,
                selectedAnswer,
              }
            : item,
        );
      }

      return [
        ...previous,
        {
          questionId,
          selectedAnswer,
        },
      ];
    });
  }, []);

  /**
   * Selected answer
   */
  const getSelectedAnswer = useCallback(
    (questionId) => {
      return (
        answers.find((item) => item.questionId === questionId)
          ?.selectedAnswer || null
      );
    },
    [answers],
  );

  /**
   * Answered question numbers
   */
  const answeredQuestions = useMemo(() => {
    return answers
      .filter((item) => item.selectedAnswer)
      .map((item) => {
        const index = questions.findIndex(
          (question) => question._id === item.questionId,
        );

        return index >= 0 ? index + 1 : null;
      })
      .filter(Boolean);
  }, [answers, questions]);

  /**
   * Check if question answered
   */
  const isAnswered = useCallback(
    (questionId) => {
      return answers.some(
        (item) => item.questionId === questionId && item.selectedAnswer,
      );
    },
    [answers],
  );

  /**
   * Backend submit format
   */
  const getSubmitPayload = useCallback(() => {
    return {
      answers: answers
        .filter((item) => item.selectedAnswer)
        .map((item) => ({
          question: item.questionId,

          selectedAnswer: item.selectedAnswer,
        })),
    };
  }, [answers]);

  /**
   * Navigation
   */
  const goToQuestion = useCallback(
    (index) => {
      if (index >= 0 && index < questions.length) {
        setCurrentQuestionIndex(index);
      }
    },
    [questions.length],
  );

  const nextQuestion = useCallback(() => {
    setCurrentQuestionIndex((previous) =>
      Math.min(previous + 1, Math.max(questions.length - 1, 0)),
    );
  }, [questions.length]);

  const previousQuestion = useCallback(() => {
    setCurrentQuestionIndex((previous) => Math.max(previous - 1, 0));
  }, []);

  /**
   * Clear attempt storage
   */
  const clearAttemptStorage = useCallback(() => {
    if (!attemptId) {
      return;
    }

    localStorage.removeItem(getStorageKey());

    setAnswers([]);
  }, [attemptId, getStorageKey]);

  return {
    // Question
    currentQuestion,

    currentQuestionIndex,

    setCurrentQuestionIndex,

    // Navigation
    goToQuestion,

    nextQuestion,

    previousQuestion,

    // Answers
    answers,

    answeredQuestions,

    isAnswered,

    handleAnswerSelect,

    getSelectedAnswer,

    // Submit
    getSubmitPayload,

    // Storage
    clearAttemptStorage,
  };
};

export default useQuizAttempt;
